import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { get_automation_url } from '@/utils/urls.ts';
import {
  AUTOMATION_BASE_KEY,
  AUTOMATION_KEY,
  AUTOMATION_LATEST_VERSION_KEY,
  AUTOMATIONS_KEY,
} from '@/utils/queryKeys.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { Automation, AutomationNotificationSettings } from '@/types/automations.ts';
import { LlmModelId, ModelPackageId, ReasoningEffortId } from '@/types/llmModels.ts';

export type UpdateAutomationParams = {
  id: string;
  name?: string;
  description?: string;
  model_package_id?: ModelPackageId;
  primary_model?: LlmModelId;
  secondary_model?: LlmModelId;
  primary_reasoning_effort?: ReasoningEffortId;
  secondary_reasoning_effort?: ReasoningEffortId;
  large_context_enabled?: boolean;
  notification_settings?: AutomationNotificationSettings;
};

const useUpdateAutomation = (): UseMutationResult<
  Automation,
  AxiosError,
  UpdateAutomationParams
> => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...params }: UpdateAutomationParams) => {
      const response = await apiClient.put(get_automation_url(id), params);
      return response.data;
    },
    onSuccess: async (automation: Automation) => {
      queryClient.setQueryData(AUTOMATION_KEY(automation.id), automation);
      await queryClient.invalidateQueries({
        queryKey: AUTOMATION_LATEST_VERSION_KEY(automation.id),
      });
      await queryClient.invalidateQueries({ queryKey: AUTOMATION_BASE_KEY(automation.id) });
      await queryClient.invalidateQueries({ queryKey: AUTOMATIONS_KEY });
    },
  });
};

export default useUpdateAutomation;
